import React, { useState } from 'react';
import { 
  DollarSign, TrendingUp, Users, Clock, Calendar, 
  Download, Filter, Building2, X, Search 
} from 'lucide-react';
import SearchableSelect from '@/client/components/admin/SearchableSelect';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

interface Transaction {
  id: string;
  userId: string;
  userName: string;
  partnerId: string;
  partnerName: string;
  bookingType: 'hotel' | 'restaurant' | 'flight';
  bookingId: string;
  amount: number;
  commission: number;
  status: 'pending' | 'confirmed' | 'cancelled';
  clickDate: string;
  conversionDate: string;
  details: {
    location: string;
    checkIn?: string;
    checkOut?: string;
    guests?: number;
    bookingDetails: string;
  };
}

const AffiliateTransactions = () => {
  useBreadcrumbs([
    { label: 'Listing Management', href: '#' },
    { label: 'Affiliate Bookings' }
  ]);
  const [filters, setFilters] = useState({
    period: 'last30days',
    status: '',
    partner: '',
    bookingType: ''
  });

  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  const [searchTerm, setSearchTerm] = useState('');

  // Sample transactions data
  const [transactions] = useState<Transaction[]>([
    {
      id: 'TRX123',
      userId: 'U1',
      userName: 'John Doe',
      partnerId: 'P1',
      partnerName: 'Booking.com',
      bookingType: 'hotel',
      bookingId: 'BK789',
      amount: 1250.00,
      commission: 125.00,
      status: 'confirmed',
      clickDate: '2024-02-14T10:30:00Z',
      conversionDate: '2024-02-14T11:15:00Z',
      details: {
        location: 'Paris, France',
        checkIn: '2024-03-15',
        checkOut: '2024-03-20',
        guests: 2,
        bookingDetails: 'Deluxe Room - Hotel Du Louvre'
      }
    },
    // Add more sample transactions
  ]);

  const stats = {
    totalRevenue: transactions.reduce((sum, t) => sum + t.amount, 0),
    totalCommission: transactions.reduce((sum, t) => sum + t.commission, 0),
    conversionRate: '12.5%',
    averageBooking: 850.00
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status: 'confirmed' | 'pending' | 'cancelled') => {
    const colors = {
      confirmed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Affiliate Transactions</h1>
              <p className="mt-1 text-sm text-gray-500">
                Monitor affiliate bookings and revenue
              </p>
            </div>
            <button 
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
              onClick={() => {/* Handle export */}}
            >
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {formatCurrency(stats.totalRevenue)}
                </h3>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Commission</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {formatCurrency(stats.totalCommission)}
                </h3>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Conversion Rate</p>
                <h3 className="text-lg font-semibold text-gray-900">{stats.conversionRate}</h3>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Average Booking</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {formatCurrency(stats.averageBooking)}
                </h3>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div className="relative flex-grow md:flex-grow-0 md:w-64">
            <input
              type="text"
              placeholder="Search transactions..."
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>

          <SearchableSelect
            options={[
              { value: 'last7days', label: 'Last 7 Days' },
              { value: 'last30days', label: 'Last 30 Days' },
              { value: 'last3months', label: 'Last 3 Months' },
              { value: 'last6months', label: 'Last 6 Months' },
              { value: 'lastYear', label: 'Last Year' }
            ]}
            value={filters.period}
            onChange={(value) => setFilters({ ...filters, period: value })}
            placeholder="Time Period"
            icon={Calendar}
            className="w-48"
          />

          <SearchableSelect
            options={[
              { value: '', label: 'All Status' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'pending', label: 'Pending' },
              { value: 'cancelled', label: 'Cancelled' }
            ]}
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            placeholder="Status"
            icon={Filter}
            className="w-48"
          />

          <SearchableSelect
            options={[
              { value: '', label: 'All Partners' },
              { value: 'booking', label: 'Booking.com' },
              { value: 'expedia', label: 'Expedia' },
              { value: 'opentable', label: 'OpenTable' }
            ]}
            value={filters.partner}
            onChange={(value) => setFilters({ ...filters, partner: value })}
            placeholder="Partner"
            icon={Building2}
            className="w-48"
          />
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Partner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {transaction.id}
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(transaction.conversionDate).toLocaleDateString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{transaction.userName}</div>
                      <div className="text-sm text-gray-500">{transaction.userId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{transaction.partnerName}</div>
                      <div className="text-sm text-gray-500">{transaction.bookingType}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(transaction.commission)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                        {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => {/* Handle view details */}}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Previous
                </button>
                <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">1</span> to{' '}
                    <span className="font-medium">10</span> of{' '}
                    <span className="font-medium">{transactions.length}</span> results
                  </p>
                </div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                    Previous
                  </button>
                  <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-blue-600 hover:bg-gray-50">
                    1
                  </button>
                  <button className="relative inline-flex items-center px-2 py-2rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      <TransactionDetailsModal 
        isOpen={selectedTransaction !== null}
        onClose={() => setSelectedTransaction(null)}
        transaction={selectedTransaction}
      />
    </div>
  );
};

// Transaction Details Modal Component
interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction
}) => {
  if (!isOpen || !transaction) return null;

  function getStatusColor(status: 'confirmed' | 'pending' | 'cancelled') {
    const colors = {
      confirmed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }
  function formatCurrency(amount: number): React.ReactNode {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Transaction Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Close</span>
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Transaction Info */}
          <div>
            <h4 className="text-sm font-medium text-gray-500">Transaction Information</h4>
            <div className="mt-2 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Transaction ID</p>
                <p className="text-sm font-medium text-gray-900">{transaction.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  getStatusColor(transaction.status)
                }`}>
                  {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Click Date</p>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(transaction.clickDate).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Conversion Date</p>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(transaction.conversionDate).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div>
            <h4 className="text-sm font-medium text-gray-500">User Information</h4>
            <div className="mt-2 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="text-sm font-medium text-gray-900">{transaction.userName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">User ID</p>
                <p className="text-sm font-medium text-gray-900">{transaction.userId}</p>
              </div>
            </div>
          </div>

          {/* Booking Details */}
          <div>
            <h4 className="text-sm font-medium text-gray-500">Booking Details</h4>
            <div className="mt-2 space-y-4">
              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="text-sm font-medium text-gray-900">{transaction.details.location}</p>
              </div>
              {transaction.details.checkIn && transaction.details.checkOut && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Check-in</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(transaction.details.checkIn).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Check-out</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(transaction.details.checkOut).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
              {transaction.details.guests && (
                <div>
                  <p className="text-sm text-gray-500">Guests</p>
                  <p className="text-sm font-medium text-gray-900">{transaction.details.guests}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Booking Details</p>
                <p className="text-sm font-medium text-gray-900">{transaction.details.bookingDetails}</p>
              </div>
            </div>
          </div>

          {/* Financial Details */}
          <div>
            <h4 className="text-sm font-medium text-gray-500">Financial Details</h4>
            <div className="mt-2 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Booking Amount</p>
                <p className="text-sm font-medium text-gray-900">{formatCurrency(transaction.amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Commission</p>
                <p className="text-sm font-medium text-gray-900">{formatCurrency(transaction.commission)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Commission Rate</p>
                <p className="text-sm font-medium text-gray-900">
                  {((transaction.commission / transaction.amount) * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          {/* Partner Info */}
          <div>
            <h4 className="text-sm font-medium text-gray-500">Partner Information</h4>
            <div className="mt-2 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Partner</p>
                <p className="text-sm font-medium text-gray-900">{transaction.partnerName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Booking Type</p>
                <p className="text-sm font-medium text-gray-900">
                  {transaction.bookingType.charAt(0).toUpperCase() + transaction.bookingType.slice(1)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Booking ID</p>
                <p className="text-sm font-medium text-gray-900">{transaction.bookingId}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AffiliateTransactions;